import type {
	IAuthToken,
	IBaseResponse,
	IGuestLogin,
	ILogin,
	IOAuthLogin,
	IOtpVerification,
	IPasswordChange,
	IPasswordForgot,
	IPasswordReset,
	IResendOtp,
	ISignup,
	ISignupResendOtp,
	ISignupVerification,
} from '@/api';
import { apiClient } from '@/api';

export const authService = {
	/**
	 * Login with username and password
	 * @param data Login credentials
	 */
	login: (data: ILogin) =>
		apiClient.post<IBaseResponse<IAuthToken>>('/login', data),

	/**
	 * Login with OAuth token (Google, Facebook, etc.)
	 * @param data OAuth login data containing idToken
	 */
	oauthLogin: (data: IOAuthLogin) =>
		apiClient.post<IBaseResponse<IAuthToken>>('/login/oauth', data),

	/**
	 * Login as guest user
	 * @param data Guest login data containing deviceCode
	 */
	guestLogin: (data: IGuestLogin) =>
		apiClient.post<IBaseResponse<IAuthToken>>('/login/guest', data),

	/**
	 * Sign up new user
	 * @param data Signup information
	 */
	signup: (data: ISignup) =>
		apiClient.post<IBaseResponse<string>>('/signup', data),

	/**
	 * Verify signup OTP
	 * @param data OTP verification data
	 */
	verifySignup: (data: ISignupVerification) =>
		apiClient.post<IBaseResponse<string>>('/signup/verify', data),

	/**
	 * Resend signup OTP
	 * @param data Registration ID for resending OTP
	 */
	resendSignupOtp: (data: ISignupResendOtp) =>
		apiClient.post<IBaseResponse<string>>('/signup/resend-otp', data),

	/**
	 * Change password for logged in user
	 * @param data Old and new password
	 */
	changePassword: (data: IPasswordChange) =>
		apiClient.put<IBaseResponse<void>>('/password/change', data),

	/**
	 * Reset password using reset ID
	 * @param data Password reset data
	 */
	resetPassword: (data: IPasswordReset) =>
		apiClient.put<IBaseResponse<void>>('/password/reset', data),

	/**
	 * Initiate forgot password process
	 * @param data User email
	 */
	forgotPassword: (data: IPasswordForgot) =>
		apiClient.put<IBaseResponse<string>>('/password/forgot', data),

	/**
	 * Verify OTP for password reset
	 * @param data OTP verification data
	 */
	verifyForgotPasswordOtp: (data: IOtpVerification) =>
		apiClient.put<IBaseResponse<string>>('/password/forgot/verify-otp', data),

	/**
	 * Resend OTP for password reset
	 * @param data Password reset ID
	 */
	resendForgotPasswordOtp: (data: IResendOtp) =>
		apiClient.put<IBaseResponse<string>>('/password/forgot/resend-otp', data),

	/**
	 * Refresh access token
	 * @param refreshToken Current refresh token
	 */
	refreshToken: (refreshToken: string) =>
		apiClient.post<IBaseResponse<IAuthToken>>('/token/refresh', {
			refreshToken,
		}),

	/**
	 * Logout current user
	 * Clears local storage and session data
	 */
	logout: () => {
		localStorage.clear();
	},
};
